import { useEffect, useMemo, useRef, useState } from "react";
import { useInView } from "motion/react";
import { useI18n } from "../i18n";
import { SectionHeading } from "./ui/card";
import { Reveal } from "./reveal";

/* Conta de 0 até o número mantendo o formato do texto original
   (prefixo "+", separador de milhar e sufixo "min" / "%"). */
function useCountUp(bruto: string, ativo: boolean): string {
  const [valor, setValor] = useState(0);

  const analise = useMemo(() => {
    const m = bruto.match(/^(\D*?)([\d.,]+)(.*)$/);
    if (!m) return null;
    const [, prefixo, numStr, sufixo] = m;
    const sep = numStr.includes(".") ? "." : numStr.includes(",") ? "," : "";
    const alvo = parseInt(numStr.replace(/[.,]/g, ""), 10);
    return { prefixo, sufixo, sep, alvo };
  }, [bruto]);

  useEffect(() => {
    if (!ativo || !analise) return;
    const inicio = performance.now();
    const duracao = 1400;
    let raf = 0;
    const tick = (agora: number) => {
      const p = Math.min((agora - inicio) / duracao, 1);
      const eased = 1 - Math.pow(1 - p, 4); // easeOutQuart
      setValor(Math.round(analise.alvo * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [ativo, analise]);

  if (!analise) return bruto;
  const sep = analise.sep;
  const corpo = sep
    ? valor.toString().replace(/\B(?=(\d{3})+(?!\d))/g, sep)
    : String(valor);
  return `${analise.prefixo}${corpo}${analise.sufixo}`;
}

function Metrica({ bruto, label, ativo }: { bruto: string; label: string; ativo: boolean }) {
  const texto = useCountUp(bruto, ativo);
  return (
    <div className="flex min-w-0 flex-col gap-1.5 px-2 py-5 sm:px-6 lg:px-4">
      <span className="whitespace-nowrap font-display text-4xl font-medium tracking-tight text-accent tabular-nums sm:text-5xl lg:text-3xl xl:text-4xl">
        {texto}
      </span>
      <span className="max-w-48 text-[13px] leading-snug text-muted-foreground">{label}</span>
    </div>
  );
}

/** Case da concessionária — citação editorial + métricas que contam ao entrar. */
export function CaseStudy() {
  const { t } = useI18n();
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-120px" });

  const metricas = [
    { v: t("case.m1.v"), l: t("case.m1.l") },
    { v: t("case.m2.v"), l: t("case.m2.l") },
    { v: t("case.m3.v"), l: t("case.m3.l") },
  ];
  const minis = t("case.mini").split("·").map((s) => s.trim());

  return (
    <section id="case" className="relative scroll-mt-24 py-24 sm:py-32">
      <div className="mx-auto max-w-6xl px-6">
        <Reveal>
          <SectionHeading
            index="06"
            label={t("case.rotulo")}
            title={t("case.titulo")}
            description={t("case.sub")}
            className="mb-12 sm:mb-16"
          />
        </Reveal>

        <Reveal delay={0.1}>
          <div className="card-paper overflow-hidden rounded-xl">
            <div className="grid gap-10 p-7 sm:p-10 lg:grid-cols-[1.1fr_1fr] lg:gap-6">
              {/* depoimento */}
              <figure className="flex flex-col justify-between gap-8">
                <blockquote className="font-display text-2xl leading-[1.25] font-medium tracking-tight text-pretty sm:text-[1.75rem]">
                  {t("case.quote")}
                </blockquote>
                <figcaption className="flex items-center gap-3.5">
                  <span className="grid size-11 place-items-center rounded-full bg-accent-soft font-display text-base font-semibold text-accent">
                    {t("case.nome").charAt(0)}
                  </span>
                  <span>
                    <span className="block text-sm font-semibold">{t("case.nome")}</span>
                    <span className="block text-[13px] text-muted-foreground">{t("case.cargo")}</span>
                  </span>
                </figcaption>
              </figure>

              {/* métricas */}
              <div
                ref={ref}
                className="grid grid-cols-1 divide-y divide-line self-center border-y border-line sm:grid-cols-3 sm:divide-x sm:divide-y-0 lg:border-0"
              >
                {metricas.map((m) => (
                  <Metrica key={m.l} bruto={m.v} label={m.l} ativo={inView} />
                ))}
              </div>
            </div>

            {/* faixa de micro-resultados */}
            <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 border-t border-line bg-surface-2/70 px-6 py-4">
              {minis.map((mini) => (
                <span
                  key={mini}
                  className="inline-flex items-center gap-2 font-mono text-[11px] text-subtle"
                >
                  <span className="size-1 rounded-full bg-accent/70" />
                  {mini}
                </span>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
